
































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import { IGetBookingDetailResponse } from '@/api/booking';
import { getParticipantName } from '@/utils/tickets';
import { AppModule } from '@/store/modules/app';
import {
  ITicketWithAmount,
  ITicketDetail,
  IssuingRuleId,
  IIssuingDetailWithValidation,
  TicketWithAmountActionEnum,
} from '@/api/types';
import {
  foregroundColor,
  formatDate,
  stringToNumber,
  translateComponentResource,
  translatePluralResource,
} from '@/utils/index';
import { Interface } from 'readline';

@Component({
  name: 'BookingDetailTicket',
  components: {
    MaterialCard,
  },
})
export default class BookingDetailTicket extends Vue {
  public static ticketIssueCountUpdatedEvent = 'update:ticket-issue-count';

  @Prop({ required: true }) data!: IGetBookingDetailResponse;
  @Prop({ required: true }) ticket!: ITicketWithAmount;
  private bgcolor = '';
  private fontcolor = '';
  private issueCountStr: string = '0';
  private issueCountRules: ((v: string) => boolean | string)[] = [];
  private issueCountItems: string[] = [];

  get appLanguage() {
    return AppModule.language;
  }

  get titleStyle() {
    return this.bgcolor
      ? {
          backgroundColor: this.bgcolor,
          color: this.fontcolor,
        }
      : {};
  }

  get issueCount() {
    return stringToNumber(this.issueCountStr);
  }

  get totalCount() {
    let count = 0;
    if (this.ticket.issuedTickets && this.ticket.issuedTickets) {
      this.ticket.issuedTickets.forEach(t => (count += t.amount!));
    }
    if (this.ticket.unissuableTickets && this.ticket.unissuableTickets) {
      this.ticket.unissuableTickets.forEach(t => (count += t.amount!));
    }
    if (this.ticket.issuableTickets && this.ticket.issuableTickets) {
      this.ticket.issuableTickets.forEach(t => (count += t.amount!));
    }
    return count;
  }

  get issuedCount() {
    let count = 0;
    if (this.ticket.issuedTickets && this.ticket.issuedTickets) {
      this.ticket.issuedTickets.forEach(t => (count += t.amount!));
    }
    return count;
  }

  get isAllActionsForbidden() {
    return (
      this.ticket.forbiddenActions &&
      this.ticket.forbiddenActions.length ===
        Object.values(TicketWithAmountActionEnum).length - 1
    );
  }

  get issuingRulesTooltip() {
    if (this.ticket.issuingRules && this.ticket.issuingRules.length > 0) {
      if (
        this.ticket.issuingRules.some(
          r =>
            (r.id as string) === (IssuingRuleId.CANNOT_PARTIALY_ISSUE as string)
        )
      ) {
        return this.$t(
          ('TicketSettingIssuingRuleEnum.' +
            IssuingRuleId.CANNOT_PARTIALY_ISSUE) as string
        );
      }
    }
    return '';
  }

  get unissuableCount() {
    let count = 0;
    if (this.ticket.unissuableTickets && this.ticket.unissuableTickets) {
      this.ticket.unissuableTickets.forEach(t => (count += t.amount!));
    }
    return count;
  }

  get issuableCount() {
    let count = 0;
    if (this.ticket.issuableTickets && this.ticket.issuableTickets) {
      this.ticket.issuableTickets.forEach(t => (count += t.amount!));
    }
    return count;
  }

  get remainingCount() {
    return this.issuableCount - this.issueCount;
  }

  get hideTicket() {
    return (
      this.isAllActionsForbidden &&
      this.unissuableCount > 0 &&
      this.issuableCount === 0
    );
  }

  mounted() {
    this.updateColors();
    this.updateRules();
    this.updateIssueCountItems();
    this.$nextTick(() => {
      this.updateIssueCountStr();
      this.handleIssueCountUpdated(this.issueCountStr);
    });
  }

  @Watch('appLanguage')
  private onLangSelected() {
    (this.$refs.issueCountForm as any).validate();
  }

  private updateColors() {
    this.bgcolor =
      this.data.uiSetting && this.data.uiSetting.bookingDetailBgcolor
        ? this.data.uiSetting.bookingDetailBgcolor
        : '';
    this.fontcolor = this.bgcolor ? foregroundColor(this.bgcolor) : '';
  }

  private updateRules() {
    const rules = [
      (v: string) =>
        (v && v.length > 0) ||
        (this.translateComponentResource(
          'Issue count must not be empty'
        ) as string),
      (v: string) =>
        stringToNumber(v) >= 0 ||
        (this.translateComponentResource(
          'Issue count must not be lower than 0'
        ) as string),
      (v: string) =>
        stringToNumber(v) <= this.issuableCount ||
        (this.translateComponentResource(
          'Issue count must not be greater than issuable count {count}',
          { count: this.issuableCount }
        ) as string),
    ];
    if (
      this.ticket.issuingRules.some(
        r =>
          (r.id as string) === (IssuingRuleId.CANNOT_PARTIALY_ISSUE as string)
      )
    ) {
      rules.push(
        (v: string) =>
          stringToNumber(v) === 0 ||
          stringToNumber(v) === this.issuableCount ||
          (this.translateComponentResource('Issue count must be 0 or {count}', {
            count: this.issuableCount,
          }) as string)
      );
    }
    this.issueCountRules = rules;
  }

  private updateIssueCountItems() {
    const items: string[] = [];
    if (
      this.ticket.issuingRules.some(
        r =>
          (r.id as string) === (IssuingRuleId.CANNOT_PARTIALY_ISSUE as string)
      )
    ) {
      items.push('0');
      items.push(this.issuableCount.toString());
    } else {
      for (let i = 0; i <= this.issuableCount; i++) {
        items.push(i.toString());
      }
    }
    this.issueCountItems = items;
  }

  private updateIssueCountStr() {
    this.issueCountStr = this.issuableCount.toString();
  }

  private translateComponentResource(
    resource: string,
    options?: { [key: string]: any }
  ) {
    return translateComponentResource(
      this.$i18n,
      this.$options.name as string,
      resource,
      options
    );
  }

  private translatePluralResource(resource: string, n: number) {
    return translatePluralResource(this.$i18n, resource, n);
  }

  private isInIssuablePeriod(unissuableTicket: ITicketDetail) {
    return (
      (!unissuableTicket.issuableFrom ||
        new Date() >= new Date(unissuableTicket.issuableFrom)) &&
      (!unissuableTicket.issuableTo ||
        new Date() <= new Date(unissuableTicket.issuableTo))
    );
  }

  private getDisobeyRuleTooltip(unissuableTicket: ITicketDetail) {
    if (
      !unissuableTicket.disobeyedRules ||
      unissuableTicket.disobeyedRules!.length <= 0
    ) {
      return '';
    }
    let text = `${this.$t('Disobey rule')} : `;
    for (let i = 0; i < unissuableTicket.disobeyedRules!.length; i++) {
      if (i === 0) {
        text += '[';
      }
      switch (unissuableTicket.disobeyedRules![i].id as IssuingRuleId) {
        case IssuingRuleId.CANNOT_PARTIALY_ISSUE:
          text += `"${this.$t(
            ('TicketSettingIssuingRuleEnum.' +
              IssuingRuleId.CANNOT_PARTIALY_ISSUE) as string
          )}"`;
          break;
        case IssuingRuleId.MUST_ISSUE_AFTER_SPECIFIC_TICKET:
          text += `"${this.$t(
            ('TicketSettingIssuingRuleEnum.' +
              IssuingRuleId.MUST_ISSUE_AFTER_SPECIFIC_TICKET) as string
          )}: ${unissuableTicket.disobeyedRules![i].targetTicketId}"`;
          break;
        default:
          text += `"${this.$t(
            ('TicketSettingIssuingRuleEnum.' +
              IssuingRuleId.UNKNOWN_RULE) as string
          )}"`;
          break;
      }
      if (i === unissuableTicket.disobeyedRules!.length - 1) {
        text += ']';
      } else {
        text += ', ';
      }
    }
    return text;
  }

  private handleIssueCountUpdated(value: string) {
    if ((this.$refs.issueCountForm as any).validate()) {
      const count = stringToNumber(value);
      this.$emit(BookingDetailTicket.ticketIssueCountUpdatedEvent, {
        ticketId: this.ticket.ticketId,
        amount: this.issueCount,
        valid: true,
      } as IIssuingDetailWithValidation);
    } else {
      this.$emit(BookingDetailTicket.ticketIssueCountUpdatedEvent, {
        ticketId: this.ticket.ticketId,
        amount: 0,
        valid: false,
      } as IIssuingDetailWithValidation);
    }
  }

  private formatDate(dateStr: string | null) {
    return dateStr ? (dateStr && formatDate(new Date(dateStr))) || '' : '';
  }

  private IssueCountItemText(value: string) {
    return `${value} ${this.translatePluralResource(
      'ticket',
      stringToNumber(value)
    )}`;
  }
}
